import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { getIntlTranslation } from '@/utils/intl';
import type { FormInstance } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl, useSelector } from 'umi';
import { CREATE_STEP_FIELDS } from './templateFormShape';

interface Options {
  form: FormInstance;
  isEdit: boolean;
  /**
   * The event the new template answers, as the base picker filters on.
   *
   * Watched off the form rather than read off the record: the create entry
   * points seed the form, and the form is what the save sends. `''` (marketing)
   * is a real value here, so the caller's fallback has to be `??`, never `||`.
   */
  baseEmailType: string | null;
  /** Cleared whenever a step is left, so a stale failure does not follow along. */
  onLeaveStep: () => void;
  /**
   * The copied base's design document, handed over in the same commit as the
   * body — a copied template has to open as the blocks it was built from, not
   * as its compiled Twig. See the drawer's note on `designDocument`.
   */
  onBaseApplied: (designDocument: unknown) => void;
}

/**
 * Creating is three steps; editing is none.
 *
 * A new template used to open straight into the three-pane editor with an
 * empty canvas and every attribute unset — the palette and the inspector are
 * meaningless before the email has a subject, and nothing on screen said what
 * to do first. So: settings, then the starting point (scratch or a copy of
 * an existing template), then content. Editing keeps landing in the editor,
 * where the settings live in their own panel and are already filled in.
 */
export function useTemplateCreateFlow({
  form,
  isEdit,
  baseEmailType,
  onLeaveStep,
  onBaseApplied,
}: Options) {
  const intl = useIntl();
  const transactionalTemplates = useSelector(
    EmailCenterSelectors.selectTransactionalTemplates,
  );
  const marketingTemplates = useSelector(
    EmailCenterSelectors.selectMarketingTemplates,
  );

  const [createStep, setCreateStep] = useState<0 | 1 | 2>(0);
  /** Starting point for the content: empty canvas or a copy of a template. */
  const [createBase, setCreateBase] = useState<'scratch' | 'template'>(
    'scratch',
  );
  const [baseTemplateId, setBaseTemplateId] = useState<string | null>(null);
  // The last applied choice — re-entering the step and continuing with the
  // same one must not wipe content already designed in the editor.
  const appliedBaseRef = useRef<string | null>(null);

  /** Step 1 of creating: the attributes, on their own, before any editor. */
  const onCreateSettings = !isEdit && createStep === 0;
  /** Step 2 of creating: choose the starting point before the editor mounts. */
  const onCreateStart = !isEdit && createStep === 1;
  /** Any create step that renders a narrow panel instead of the editor. */
  const onCreatePanel = onCreateSettings || onCreateStart;

  /**
   * Templates the starting-point picker offers as a base.
   *
   * Two filters, both about what a copy is worth:
   *
   * - Same `email_type`. A base is copied for its content, and content is
   *   written for one event — an appointment reminder is no starting point for
   *   a cancellation mail. Marketing templates carry `''`, so the same
   *   comparison also keeps the two kinds apart without a second rule.
   * - Owned or public only. The list endpoint is `visibleToPartner`, which is
   *   wider than that: it also returns another partner's non-public master
   *   whenever one of this partner's journeys links it. Those are visible so
   *   the journey can show what it sends, not to be copied from.
   */
  const baseTemplateCandidates = useMemo(() => {
    const templates = [...transactionalTemplates, ...marketingTemplates].filter(
      (template) => template.is_owned || template.is_public,
    );
    // Unknown type (no preset, which the create entry points always give):
    // filtering on it would empty the list rather than narrow it.
    if (baseEmailType == null) return templates;
    return templates.filter(
      (template) => template.email_type === baseEmailType,
    );
  }, [transactionalTemplates, marketingTemplates, baseEmailType]);

  const baseTemplateOptions = useMemo(() => {
    const masterSuffix = getIntlTranslation(intl, {
      id: 'pages.emailCenter.templates.masterBadge',
      defaultMessage: 'Master',
    });
    return baseTemplateCandidates.map((template) => ({
      value: template.id,
      label: template.is_owned
        ? template.name
        : `${template.name} · ${masterSuffix}`,
    }));
  }, [baseTemplateCandidates, intl]);

  /**
   * Settings → starting point.
   *
   * Validates only the attribute fields, never `body`: the body is empty at this
   * point by definition, and its `required` rule would refuse to let anyone reach
   * the editor that fills it.
   */
  const goToStartChoice = useCallback(async () => {
    try {
      await form.validateFields(CREATE_STEP_FIELDS);
    } catch {
      // antd has already marked the offending fields; nothing to add.
      return;
    }
    onLeaveStep();
    setCreateStep(1);
  }, [form, onLeaveStep]);

  /**
   * Starting point → editor.
   *
   * Copying the base happens exactly once per distinct choice: going back to
   * this step and continuing with the same selection must never wipe content
   * already designed in the editor. The copy is a plain snapshot of the base's
   * body (and its layout) — the new template has no link to the base.
   */
  const startDesigning = useCallback(() => {
    const choiceKey =
      createBase === 'template' ? `template:${baseTemplateId}` : 'scratch';
    if (appliedBaseRef.current !== choiceKey) {
      const base =
        createBase === 'template' && baseTemplateId
          ? baseTemplateCandidates.find(
              (template) => template.id === baseTemplateId,
            )
          : null;
      form.setFieldsValue({
        body: base?.body ?? '',
        design_document: base?.design_document ?? null,
        ...(base?.layout_key ? { layout_key: base.layout_key } : {}),
      });
      onBaseApplied(base?.design_document ?? null);
      appliedBaseRef.current = choiceKey;
    }
    onLeaveStep();
    setCreateStep(2);
  }, [
    createBase,
    baseTemplateId,
    baseTemplateCandidates,
    form,
    onLeaveStep,
    onBaseApplied,
  ]);

  /** Back to step 1 with nothing carried over — a fresh create in the same drawer. */
  const resetCreateFlow = useCallback(() => {
    setCreateStep(0);
    setCreateBase('scratch');
    setBaseTemplateId(null);
    appliedBaseRef.current = null;
  }, []);

  return {
    createStep,
    setCreateStep,
    createBase,
    setCreateBase,
    baseTemplateId,
    setBaseTemplateId,
    baseTemplateOptions,
    onCreateSettings,
    onCreateStart,
    onCreatePanel,
    goToStartChoice,
    startDesigning,
    resetCreateFlow,
  };
}

export default useTemplateCreateFlow;
